import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ExternalLink,
  Eye,
  Heart,
  Info,
  Plus,
} from "lucide-react";
import { toast } from "sonner";
import { Img } from "../common/Img";
import { Reveal } from "../common/Reveal";
import { Carousel, CarouselContent, CarouselItem } from "../ui/carousel";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";
import ProductActions from "../common/ProductActions";
import { FEATURES } from "../../config/features";
import { NEW_VARIETIES, NEW_VARIETIES_URL } from "../../data/newVarieties";
import { useLanguage } from "../../context/LanguageContext";
import { useFavorites } from "../../context/FavoritesContext";
import { useCart } from "../../context/CartContext";

const AUTOPLAY_MS = 5400;

function toTitleCase(text) {
  return text
    .toLowerCase()
    .split(" ")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

export default function NewVarieties() {
  const { t, localize } = useLanguage();
  const navigate = useNavigate();
  const fav = useFavorites();
  const cart = useCart();
  const [api, setApi] = useState(null);
  const [isPaused, setIsPaused] = useState(false);
  const [detailIndex, setDetailIndex] = useState(null);
  const [infoCardId, setInfoCardId] = useState(null);

  // Task 25 — action labels come from the central i18n dictionaries (5 languages).
  const copy = useMemo(
    () => ({
      info: t("actions.info"),
      quickView: t("actions.quickView"),
      openCollection: t("actions.openCollection"),
      viewCollection: t("actions.viewCollection"),
      details: t("actions.details"),
      addFavorite: t("actions.addFav"),
      removeFavorite: t("actions.removeFav"),
      addedFav: t("actions.addedFav"),
      removedFav: t("actions.removedFav"),
      addRequest: t("actions.addRequest"),
      inRequest: t("actions.inRequest"),
      addedToRequest: t("actions.added"),
    }),
    [t]
  );

  // Task 06 (C06) — preview actions reference the real catalog variety so
  // favorites, order requests and the details page all work end-to-end.
  const varietyRef = useCallback(
    (v) => ({ id: v.id, name: toTitleCase(v.name), image: v.image, category_label: "Carnations" }),
    []
  );

  const onFavorite = useCallback(
    (e, v) => {
      e?.stopPropagation();
      const wasFav = fav.isFavorite(v.id);
      fav.toggle(varietyRef(v));
      toast(wasFav ? copy.removedFav : copy.addedFav);
    },
    [fav, copy, varietyRef]
  );

  const onAddToRequest = useCallback(
    (e, v) => {
      e?.stopPropagation();
      cart.addLine(varietyRef(v));
      toast.success(copy.addedToRequest, { description: toTitleCase(v.name) });
    },
    [cart, copy, varietyRef]
  );

  const openFullDetails = useCallback(
    (v) => {
      setDetailIndex(null);
      navigate(`/variety/${v.id}`);
    },
    [navigate]
  );

  useEffect(() => {
    if (!api || isPaused || detailIndex !== null) return undefined;

    const interval = window.setInterval(() => {
      api.scrollNext();
    }, AUTOPLAY_MS);

    return () => window.clearInterval(interval);
  }, [api, isPaused, detailIndex]);

  const previous = useCallback(() => api?.scrollPrev(), [api]);
  const next = useCallback(() => api?.scrollNext(), [api]);

  const openDetails = useCallback((index) => {
    setDetailIndex(index);
  }, []);

  const closeDetails = useCallback(() => {
    setDetailIndex(null);
  }, []);

  const showPreviousDetails = useCallback(() => {
    setDetailIndex((current) =>
      current === null ? 0 : (current - 1 + NEW_VARIETIES.length) % NEW_VARIETIES.length
    );
  }, []);

  const showNextDetails = useCallback(() => {
    setDetailIndex((current) =>
      current === null ? 0 : (current + 1) % NEW_VARIETIES.length
    );
  }, []);

  const selectedVariety = detailIndex === null ? null : NEW_VARIETIES[detailIndex];

  return (
    <>
      <section
        className="relative overflow-hidden bg-white py-20 md:py-28"
        data-testid="new-varieties-section"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        <div className="container-tessa">
          <Reveal className="mx-auto max-w-2xl text-center">
            <span className="font-sans text-[11px] uppercase tracking-[0.24em] text-[#D92A47] md:text-xs">
              {t("newVarieties.eyebrow")}
            </span>
            <h2 className="mt-3 font-serif text-4xl italic leading-none text-[#252525] md:text-6xl">
              {t("newVarieties.title")}
            </h2>
            <p className="mx-auto mt-5 max-w-xl text-sm leading-relaxed text-[#817B74] md:text-base">
              {t("newVarieties.description")}
            </p>
          </Reveal>

          <div className="relative mt-10 md:mt-14">
            <Carousel
              setApi={setApi}
              opts={{ align: "start", loop: true, skipSnaps: false }}
              aria-label={t("newVarieties.title")}
            >
              <CarouselContent className="-ml-3 md:-ml-5">
                {NEW_VARIETIES.map((variety, index) => {
                  const isInfoOpen = infoCardId === variety.id;
                  const isFav = fav.isFavorite(variety.id);
                  const inCart = cart.lines.some((l) => l.variety_id === variety.id);

                  return (
                    <CarouselItem
                      key={variety.id}
                      className="basis-[88%] pl-3 sm:basis-1/2 md:pl-5 xl:basis-1/4"
                    >
                      <article
                        className="group relative text-center"
                        data-testid={`new-variety-${variety.id}`}
                      >
                        <div className="relative aspect-square overflow-hidden rounded-[28px] bg-[#F4F1EB]">
                          <button
                            type="button"
                            onClick={() => openDetails(index)}
                            className="block h-full w-full"
                            aria-label={`${copy.quickView}: ${variety.name}`}
                          >
                            <Img
                              src={variety.image}
                              alt={variety.name}
                              className="h-full w-full object-contain px-4 py-4 transition-transform duration-700 ease-out group-hover:scale-[1.03]"
                            />
                          </button>

                          <div className="absolute right-3 top-1/2 z-10 flex -translate-y-1/2 flex-col gap-2 opacity-100 transition-opacity duration-300 md:opacity-0 md:group-hover:opacity-100 md:group-focus-within:opacity-100">
                            <button
                              type="button"
                              title={copy.info}
                              aria-label={`${copy.info}: ${variety.name}`}
                              onClick={() =>
                                setInfoCardId((current) =>
                                  current === variety.id ? null : variety.id
                                )
                              }
                              className="grid h-11 w-11 place-items-center rounded-sm border border-black/10 bg-white/96 text-[#252525] shadow-sm transition-colors hover:bg-[#D92A47] hover:text-white"
                            >
                              <Info size={18} />
                            </button>
                            <button
                              type="button"
                              title={copy.quickView}
                              aria-label={`${copy.quickView}: ${variety.name}`}
                              onClick={() => openDetails(index)}
                              className="grid h-11 w-11 place-items-center rounded-sm bg-[#D92A47] text-white shadow-sm transition-colors hover:bg-[#b71e3a]"
                            >
                              <Eye size={18} />
                            </button>
                            {FEATURES.favorites && (
                            <button
                              type="button"
                              title={isFav ? copy.removeFavorite : copy.addFavorite}
                              aria-label={`${isFav ? copy.removeFavorite : copy.addFavorite}: ${variety.name}`}
                              aria-pressed={isFav}
                              data-testid={`home-fav-${variety.id}`}
                              onClick={(e) => onFavorite(e, variety)}
                              className={`grid h-11 w-11 place-items-center rounded-sm border shadow-sm transition-colors ${
                                isFav
                                  ? "border-[#D92A47] bg-[#D92A47] text-white"
                                  : "border-black/10 bg-white/96 text-[#252525] hover:bg-[#D92A47] hover:text-white"
                              }`}
                            >
                              <Heart size={18} fill={isFav ? "currentColor" : "none"} />
                            </button>
                            )}
                            {FEATURES.orderRequests && (
                            <button
                              type="button"
                              title={inCart ? copy.inRequest : copy.addRequest}
                              aria-label={`${inCart ? copy.inRequest : copy.addRequest}: ${variety.name}`}
                              aria-pressed={inCart}
                              data-testid={`home-add-${variety.id}`}
                              onClick={(e) => onAddToRequest(e, variety)}
                              className={`grid h-11 w-11 place-items-center rounded-sm border shadow-sm transition-colors ${
                                inCart
                                  ? "border-[#C4A46B] bg-[#C4A46B] text-black"
                                  : "border-black/10 bg-white/96 text-[#252525] hover:bg-[#252525] hover:text-white"
                              }`}
                            >
                              {inCart ? <Check size={18} /> : <Plus size={18} />}
                            </button>
                            )}
                            <a
                              href={NEW_VARIETIES_URL}
                              title={copy.openCollection}
                              aria-label={`${copy.openCollection}: ${variety.name}`}
                              target="_blank"
                              rel="noreferrer"
                              className="grid h-11 w-11 place-items-center rounded-sm border border-black/10 bg-white/96 text-[#252525] shadow-sm transition-colors hover:bg-[#252525] hover:text-white"
                            >
                              <ExternalLink size={18} />
                            </a>
                          </div>
                        </div>

                        <h3 className="mt-4 font-serif text-sm uppercase tracking-[0.06em] text-[#252525] md:text-base">
                          {variety.name}
                        </h3>

                        <div
                          className={`mx-auto mt-3 max-w-[280px] overflow-hidden rounded-2xl border border-black/6 bg-[#F8F5EF] px-4 text-left transition-all duration-400 ${
                            isInfoOpen
                              ? "max-h-28 py-3 opacity-100"
                              : "max-h-0 py-0 opacity-0"
                          }`}
                        >
                          <span className="block font-sans text-[10px] uppercase tracking-[0.22em] text-[#D92A47]">
                            {copy.info}
                          </span>
                          <p className="mt-2 text-sm leading-relaxed text-[#6F6861]">
                            {localize(variety, "note")}
                          </p>
                        </div>
                      </article>
                    </CarouselItem>
                  );
                })}
              </CarouselContent>
            </Carousel>

            <button
              type="button"
              onClick={previous}
              aria-label={t("newVarieties.previous")}
              className="absolute left-0 top-[42%] z-10 grid h-10 w-10 -translate-x-2 -translate-y-1/2 place-items-center rounded-full border border-[#252525]/35 bg-white/90 text-[#252525] shadow-sm backdrop-blur transition-colors hover:border-[#D92A47] hover:bg-[#D92A47] hover:text-white md:-translate-x-5"
            >
              <ArrowLeft size={17} />
            </button>
            <button
              type="button"
              onClick={next}
              aria-label={t("newVarieties.next")}
              className="absolute right-0 top-[42%] z-10 grid h-10 w-10 translate-x-2 -translate-y-1/2 place-items-center rounded-full border border-[#252525]/35 bg-white/90 text-[#252525] shadow-sm backdrop-blur transition-colors hover:border-[#D92A47] hover:bg-[#D92A47] hover:text-white md:translate-x-5"
            >
              <ArrowRight size={17} />
            </button>
          </div>

          <Reveal className="mt-10 text-center">
            <a
              href={NEW_VARIETIES_URL}
              target="_blank"
              rel="noreferrer"
              className="group inline-flex items-center gap-2 border-b border-[#C4A46B] pb-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#252525] transition-colors hover:text-[#D92A47] md:text-xs"
            >
              {t("newVarieties.viewCollection")}
              <ExternalLink
                size={14}
                className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
              />
            </a>
          </Reveal>
        </div>
      </section>

      <Dialog open={detailIndex !== null} onOpenChange={(open) => !open && closeDetails()}>
        {selectedVariety ? (
          <DialogContent className="max-w-6xl border-none bg-[#F6F2EC] p-0 sm:rounded-none md:rounded-none">
            <DialogTitle className="sr-only">{toTitleCase(selectedVariety.name)}</DialogTitle>
            <div className="grid grid-cols-1 md:grid-cols-[1.05fr_0.95fr]">
              <div className="relative min-h-[320px] bg-[#F1EEE8] md:min-h-[620px]">
                <Img
                  src={selectedVariety.image}
                  alt={selectedVariety.name}
                  className="absolute inset-0 h-full w-full object-contain p-8 md:p-10"
                />

                <button
                  type="button"
                  onClick={showPreviousDetails}
                  aria-label={t("newVarieties.previous")}
                  className="absolute left-4 top-1/2 z-10 grid h-12 w-12 -translate-y-1/2 place-items-center bg-white/95 text-[#252525] shadow-sm transition-colors hover:bg-[#D92A47] hover:text-white"
                >
                  <ArrowLeft size={20} />
                </button>
                <button
                  type="button"
                  onClick={showNextDetails}
                  aria-label={t("newVarieties.next")}
                  className="absolute right-4 top-1/2 z-10 grid h-12 w-12 -translate-y-1/2 place-items-center bg-white/95 text-[#252525] shadow-sm transition-colors hover:bg-[#D92A47] hover:text-white"
                >
                  <ArrowRight size={20} />
                </button>

                <div className="absolute bottom-6 left-1/2 flex -translate-x-1/2 items-center gap-2">
                  {NEW_VARIETIES.map((variety, index) => (
                    <button
                      key={variety.id}
                      type="button"
                      onClick={() => setDetailIndex(index)}
                      aria-label={variety.name}
                      className={`h-2.5 w-2.5 rounded-full transition-colors ${
                        detailIndex === index ? "bg-[#252525]" : "bg-black/25 hover:bg-black/45"
                      }`}
                    />
                  ))}
                </div>
              </div>

              <div className="flex flex-col justify-center px-8 py-10 md:px-12 md:py-14">
                <span className="font-sans text-[11px] uppercase tracking-[0.24em] text-[#D92A47]">
                  {t("newVarieties.eyebrow")}
                </span>
                <h3 className="mt-3 font-serif text-4xl text-[#252525] md:text-5xl">
                  {toTitleCase(selectedVariety.name)}
                </h3>
                <p className="mt-8 max-w-xl text-lg leading-relaxed text-[#5B5550]">
                  {localize(selectedVariety, "description")}
                </p>

                <div className="mt-10 flex flex-wrap items-center gap-4">
                  <button
                    type="button"
                    onClick={() => openFullDetails(selectedVariety)}
                    data-testid={`home-details-${selectedVariety.id}`}
                    className="inline-flex items-center gap-2 bg-[#252525] px-5 py-3 text-[12px] font-semibold uppercase tracking-[0.16em] text-white transition-colors hover:bg-[#D92A47]"
                  >
                    {copy.details}
                    <ArrowRight size={15} />
                  </button>
                  <ProductActions dense variety={varietyRef(selectedVariety)} />
                </div>

                <div className="mt-4 flex flex-wrap items-center gap-4">
                  <a
                    href={NEW_VARIETIES_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 border border-black/15 px-5 py-3 text-[12px] font-semibold uppercase tracking-[0.16em] text-[#252525] transition-colors hover:border-[#D92A47] hover:text-[#D92A47]"
                  >
                    {copy.viewCollection}
                    <ExternalLink size={15} />
                  </a>
                </div>
              </div>
            </div>
          </DialogContent>
        ) : null}
      </Dialog>
    </>
  );
}
